import * as React from 'react';
import { DatasheetGetDto } from '@common/dto/datasheet/get-dto';
import { useTabsController } from './tabs';
import { DatasheetElement, DatasheetElementId, DatasheetElementType } from '@modules/datasheets/types/datasheet-element';
import { DatasheetGridSettingsMarginType } from '@common/dto/datasheet/grid-settings-dto';
import { DatasheetStorage, isServerDerivedDatasheetField } from '@modules/datasheets/utils/datasheet-storage';
import { useDatasheetUndoRedoController } from './undo-redo';
import { generateId } from '@common/utils/common';
import { DatasheetTabDto } from '@common/dto/datasheet/tab-dto';
import { buildDatasheetElementsByTab } from '@modules/datasheets/utils/datasheet-elements';

type BuildElementsTabsArg = Parameters<typeof buildDatasheetElementsByTab>[0];
import { updateFieldValue, UpdateFieldValueParams, UpdateFieldValueResponse, addDatasheetTableRow, deleteDatasheetTableRow } from '@modules/datasheets/services/datasheet';
import { refreshPedigreeSubtextAndSymbols } from '@common/utils/ui-bridge';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';
import { FieldType } from '@common/dto/field/type';
import { useMessage } from '@common/hooks/message';
import { useFieldValidationState } from '../components/datasheet/context';
import { trDts } from '@modules/datasheets/utils/i18n';

function getFlatFieldEffectiveValue(
  field: { name?: unknown; value?: unknown } | undefined,
  fieldType: number | undefined
): string {
  if (!field) return '';
  if (
    fieldType === FieldType.dropdown
    || fieldType === FieldType.checkbox
    || fieldType === FieldType.lookupTable
  ) {
    const v = field.value;
    if (v === undefined || v === null) return '';
    return String(v);
  }
  const v = field.name ?? field.value ?? '';
  if (v === undefined || v === null) return '';
  return String(v);
}

function shouldCallLoadTabDataAfterUndoRedo(tableFieldId?: number): boolean {
  return !(tableFieldId != null && tableFieldId > 0);
}

type Props = {
  datasheet: DatasheetGetDto;
  onDatasheetChange: (datasheet: DatasheetGetDto) => void;
  /** When true, field edits and undo/redo that sync to the server are disabled (e.g. IDS/PDS locked by another user). */
  datasheetEditLocked?: boolean;
  context?: number;
  globalId?: number;
  /** Base IDS/PDS context (1 or 2) for table datasheet updates. */
  tableContext?: number;
  /** Parent table field id for TDS result (context=8). */
  tableFieldId?: number;
  /** Currently selected row rrn for TDS result (context=8). */
  currentTdsRrn?: number;
  loadTabData?: (tabId: number, options?: { forceRefresh?: boolean; silent?: boolean }) => Promise<void> | void;
  loadTabFormat?: (tabId: number) => Promise<void> | void;
  /** When true, datasheet is in pedigree split view; refresh subtext/symbols after updateValue */
  formatOnly?: boolean;
  preventTableDataPreservationRef?: React.MutableRefObject<boolean>;
  initialTabId?: number;
  onTabChange?: (tabId: number | undefined) => void;
  notifyTableSyncMarkerConsumed?: () => void;
};

export const useDatasheetController = (props: Props) => {
  const { datasheet, onDatasheetChange, datasheetEditLocked, context, globalId, tableContext, tableFieldId, currentTdsRrn, loadTabData, loadTabFormat, formatOnly, preventTableDataPreservationRef, initialTabId, onTabChange, notifyTableSyncMarkerConsumed } = props;
  const [currentTabId, setCurrentTabId] = React.useState(datasheet.tabs[0]?.id);
  const tabsController = useTabsController({ datasheet, onDatasheetChange });
  const { showMessage, closeMessage, messageProps: validationMessageProps } = useMessage();
  const fieldValidationState = useFieldValidationState();
  const [datasheetElements, setDatasheetElements] = React.useState<Record<DatasheetElementId, DatasheetElement>>({});
  const datasheetElementsStorage = React.useMemo<DatasheetStorage>(() => new DatasheetStorage(), []);
  const [currentDatasheetElementsHistoryRecordId, setCurrentDatasheetElementsHistoryRecordId] = React.useState<string>();
  const currentTabIdRef = React.useRef(currentTabId);
  const currentHistoryRecordIdRef = React.useRef<string | undefined>(currentDatasheetElementsHistoryRecordId);
  const datasheetElementsRef = React.useRef<Record<DatasheetElementId, DatasheetElement>>(datasheetElements);
  const isUndoRedoInProgressRef = React.useRef(false);
  /** Serialize field updates so baseElements always reflects prior edits (prevents first name + DOB merging into one undo step). */
  const updatesQueueRef = React.useRef<Promise<void>>(Promise.resolve());
  const onTabChangeRef = React.useRef(onTabChange);

  React.useEffect(() => {
    onTabChangeRef.current = onTabChange;
  }, [onTabChange]);

  const loadTabDataRef = React.useRef(loadTabData);
  const loadTabFormatRef = React.useRef(loadTabFormat);
  React.useEffect(() => {
    loadTabDataRef.current = loadTabData;
    loadTabFormatRef.current = loadTabFormat;
  }, [loadTabData, loadTabFormat]);

  React.useEffect(() => {
    currentTabIdRef.current = currentTabId;
  }, [currentTabId]);

  React.useEffect(() => {
    currentHistoryRecordIdRef.current = currentDatasheetElementsHistoryRecordId;
  }, [currentDatasheetElementsHistoryRecordId]);

  React.useEffect(() => {
    datasheetElementsRef.current = datasheetElements;
  }, [datasheetElements]);

  const undoRedoController = useDatasheetUndoRedoController({
    currentHistoryRecordId: currentDatasheetElementsHistoryRecordId,
    getDatasheetElementsHistorySequence: () => datasheetElementsStorage.getHistorySequence(currentTabId!),
  });
  // Track the previous format ID to detect format changes
  const previousFormatIdRef = React.useRef<number | undefined>(datasheet.id);
  // We only want to run the initial tab/elements bootstrap once per format. 
  // When format changes, we need to reset and bootstrap again.
  const isInitialTabsBootstrapDoneRef = React.useRef(false);

  const setTabDatasheetElements = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: DatasheetTabDto['id']) => {
    const resolvedTabId = tabId ?? currentTabIdRef.current;
    if (resolvedTabId == null) {
      return;
    }
    datasheetElementsStorage.setElements(resolvedTabId, elements);
    datasheetElementsRef.current = elements;
    setDatasheetElements(elements);
  }, []);

  const addNewDatasheetElementsHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: DatasheetTabDto['id']) => {
    const resolvedTabId = tabId ?? currentTabIdRef.current;
    if (resolvedTabId == null) {
      return;
    }
    const newHistoryRecordId = generateId();

    datasheetElementsStorage.addHistoryRecord({
      tabId: resolvedTabId,
      currentHistoryRecordId: currentHistoryRecordIdRef.current,
      newHistoryRecordId,
      elementsForNewHistoryRecord: elements
    });

    currentHistoryRecordIdRef.current = newHistoryRecordId;
    setCurrentDatasheetElementsHistoryRecordId(newHistoryRecordId);
  }, []);

  const setTabDatasheetElementsAndAddHistoryRecord = React.useCallback((elements: Record<DatasheetElementId, DatasheetElement>, tabId?: DatasheetTabDto['id']) => {
    setTabDatasheetElements(elements, tabId);
    addNewDatasheetElementsHistoryRecord(elements, tabId);
  }, [setTabDatasheetElements, addNewDatasheetElementsHistoryRecord]);

  // Reset storage and bootstrap state when format ID changes
  React.useEffect(() => {
    // Check if format has changed (format ID is different and not initial load)
    if (previousFormatIdRef.current !== undefined && 
        previousFormatIdRef.current !== 0 && 
        previousFormatIdRef.current !== datasheet.id && 
        datasheet.id !== 0) {
      // Format has changed - clear everything and reset
      datasheetElementsRef.current = {};
      setDatasheetElements({});
      currentHistoryRecordIdRef.current = undefined;
      setCurrentDatasheetElementsHistoryRecordId(undefined);
      // Reset bootstrap flag so initial bootstrap runs again for new format
      isInitialTabsBootstrapDoneRef.current = false;
      if (datasheet.tabs.length > 0 && datasheet.tabs[0]) {
        setCurrentTabId(datasheet.tabs[0].id);
      }
    }
    previousFormatIdRef.current = datasheet.id;
  }, [datasheet.id, datasheet.tabs]);

  // When tabs (and their data) change (e.g. after loadTabData),
  // rebuild elements so updated backend values are reflected.
  // Skip overwriting current tab's display during undo/redo (matches GWT: restore from stack takes precedence).
  React.useEffect(() => {
    if (!datasheet.tabs.length) {
      return;
    }
    if (isUndoRedoInProgressRef.current) {
      return;
    }

    const elementsByTab = buildDatasheetElementsByTab(datasheet.tabs as unknown as BuildElementsTabsArg);

    datasheet.tabs.forEach((tab) => {
      const tabElements = elementsByTab[String(tab.id)] || {};
      // When loadTabData runs after a flat field blur, the API response may not yet include
      // recent table cell edits. Preserve table field tableData from current display so
      // we don't overwrite and cause an "automatic undo" appearance for the table.
      // Skip when tableFieldId (TDS): we load per-row data and must use fresh API response.
      //
      // When refreshActiveTabData sets preventTableDataPreservationRef (focus/click after DS_TABLE_SYNC_MARKER),
      // we still need currentElements for a *selective* merge: if the API returns no rows for a table field
      // but the UI still has rows (common with multi-table tabs / partial payloads), keep the in-memory
      // tableData so dropdowns and other cells in the other table are not cleared.
      const currentElements = !tableFieldId
        ? datasheetElementsStorage.getElements(tab.id)
        : undefined;
      const mergedElements = { ...tabElements };
      const forceRefreshTablesFromApi = Boolean(preventTableDataPreservationRef?.current);

      if (currentElements) {
        for (const id of Object.keys(mergedElements) as DatasheetElementId[]) {
          const el = mergedElements[id];
          if (el?.type === DatasheetElementType.field) {
            const fieldOriginal = (el as DatasheetElement<DatasheetFieldComponentGetDto>).original?.field as
              | { type?: number; fieldtype?: { typeid?: number }; table?: boolean }
              | undefined;
            const isTableField =
              fieldOriginal?.type === FieldType.table
              || fieldOriginal?.fieldtype?.typeid === FieldType.table
              || fieldOriginal?.table === true;
            if (isTableField) {
              const currentEl = currentElements[id];
              const currentTableData = currentEl?.type === DatasheetElementType.field
                ? ((currentEl as DatasheetElement<DatasheetFieldComponentGetDto>).original?.field as { tableData?: unknown[] })?.tableData
                : undefined;
              const apiTableData = ((el as DatasheetElement<DatasheetFieldComponentGetDto>).original?.field as { tableData?: unknown[] })?.tableData;
              const apiHasRows = Array.isArray(apiTableData) && apiTableData.length > 0;

              if (forceRefreshTablesFromApi) {
                if (!apiHasRows && Array.isArray(currentTableData) && currentTableData.length > 0) {
                  mergedElements[id] = {
                    ...el,
                    original: {
                      ...(el as DatasheetElement<DatasheetFieldComponentGetDto>).original,
                      field: {
                        ...(el as DatasheetElement<DatasheetFieldComponentGetDto>).original.field,
                        tableData: currentTableData,
                      },
                    },
                  } as DatasheetElement;
                }
              } else if (Array.isArray(currentTableData)) {
                mergedElements[id] = {
                  ...el,
                  original: {
                    ...(el as DatasheetElement<DatasheetFieldComponentGetDto>).original,
                    field: {
                      ...(el as DatasheetElement<DatasheetFieldComponentGetDto>).original.field,
                      tableData: currentTableData,
                    },
                  },
                } as DatasheetElement;
              }
            }
          }
        }
      }

      datasheetElementsStorage.setElements(tab.id, mergedElements);

      if (String(tab.id) === String(currentTabId)) {
        const tabWithData = tab as DatasheetTabDto & { data?: unknown[] };
        const tabHasData = tabWithData.data !== undefined && Array.isArray(tabWithData.data);
        if (tabHasData) {
          setTabDatasheetElements(mergedElements, tab.id);
          const currentHistoryId = currentHistoryRecordIdRef.current;
          if (currentHistoryId) {
            datasheetElementsStorage.patchServerDerivedFieldsIntoHistoryRecord(
              tab.id,
              currentHistoryId,
              mergedElements
            );
          }
        }
      }
    });
    if (preventTableDataPreservationRef?.current) {
      preventTableDataPreservationRef.current = false;
    }
  }, [datasheet.tabs, currentTabId, setTabDatasheetElements, datasheetElementsStorage, tableFieldId]);

  React.useEffect(() => {
    if (isInitialTabsBootstrapDoneRef.current) {
      return;
    }

    if (!datasheet.tabs.length) {
      return;
    }

    const firstTab = datasheet.tabs[0];
    if (!firstTab) {
      return;
    }

    const elementsByTab = buildDatasheetElementsByTab(datasheet.tabs as unknown as BuildElementsTabsArg);
    datasheet.tabs.forEach((tab) => {
      datasheetElementsStorage.addTab(tab.id);
    });

    const resolvedFromPreference =
      initialTabId != null
        ? datasheet.tabs.find((t) => Number(t.id) === Number(initialTabId))
        : undefined;
    const selectedTab = resolvedFromPreference ?? firstTab;

    const selectedTabElements = elementsByTab[String(selectedTab.id)] || {};
    setCurrentTabId(selectedTab.id);
    onTabChangeRef.current?.(Number(selectedTab.id));
    datasheetElementsStorage.setElements(selectedTab.id, selectedTabElements);
    setDatasheetElements(selectedTabElements);

    const initialHistoryId = generateId();
    datasheetElementsStorage.addHistoryRecord({
      tabId: selectedTab.id,
      currentHistoryRecordId: undefined,
      newHistoryRecordId: initialHistoryId,
      elementsForNewHistoryRecord: selectedTabElements,
    });
    currentHistoryRecordIdRef.current = initialHistoryId;
    setCurrentDatasheetElementsHistoryRecordId(initialHistoryId);

    datasheet.tabs.forEach((tab) => {
      if (Number(tab.id) === Number(selectedTab.id)) {
        return;
      }
      const tabElements = elementsByTab[String(tab.id)] || {};
      datasheetElementsStorage.setElements(tab.id, tabElements);

      const historyRecordId = generateId();
      datasheetElementsStorage.addHistoryRecord({
        tabId: tab.id,
        currentHistoryRecordId: undefined,
        newHistoryRecordId: historyRecordId,
        elementsForNewHistoryRecord: tabElements,
      });
    });

    isInitialTabsBootstrapDoneRef.current = true;

    const loadData = loadTabDataRef.current;
    const loadFmt = loadTabFormatRef.current;
    if (loadData && Number(selectedTab.id) !== Number(firstTab.id)) {
      const persistedNonFirstId = Number(selectedTab.id);
      void (async () => {
        if (loadFmt) {
          await loadFmt(persistedNonFirstId);
        }
        await loadData(persistedNonFirstId, { forceRefresh: true });
      })();
    }
  }, [datasheet.tabs, datasheet.id, initialTabId]);

  const handleSelectedTabChange = (tabId: DatasheetGetDto['id']) => {
    if (currentTabIdRef.current != null && Number(tabId) === Number(currentTabIdRef.current)) {
      return;
    }

    if (currentTabIdRef.current && currentHistoryRecordIdRef.current) {
      datasheetElementsStorage.setCurrentHistoryRecordId(currentTabIdRef.current, currentHistoryRecordIdRef.current);
    }

    if (loadTabFormat) {
      loadTabFormat(Number(tabId));
    }

    setCurrentTabId(tabId);
    onTabChangeRef.current?.(Number(tabId));
    const currentHistoryRecordOfNewlySelectedTabId =
      datasheetElementsStorage.getCurrentHistoryRecordId(tabId);
    const storedElements = datasheetElementsStorage.getElements(tabId);
    const defaultElements = currentHistoryRecordOfNewlySelectedTabId
      ? datasheetElementsStorage.getHistoryRecord(
          tabId,
          currentHistoryRecordOfNewlySelectedTabId
        )
       : storedElements;

    const newElements = defaultElements || {};
    // When switching to a tab that has no stored elements yet, don't replace content — keep previous tab
    // visible so the background doesn't blink. loadTabData will update state and the effect will set elements when done.
    const hasStoredElements = typeof newElements === 'object' && Object.keys(newElements).length > 0;
    if (hasStoredElements) {
      setTabDatasheetElements(newElements, tabId);
    }

    if (currentHistoryRecordOfNewlySelectedTabId) {
      currentHistoryRecordIdRef.current = currentHistoryRecordOfNewlySelectedTabId;
      setCurrentDatasheetElementsHistoryRecordId(currentHistoryRecordOfNewlySelectedTabId);
    } else {
      addNewDatasheetElementsHistoryRecord(newElements, tabId);
    }

    if (loadTabData) {
      loadTabData(Number(tabId));
    }
  };

  const blurActiveInputIfAny = React.useCallback(() => {
    if (typeof document === 'undefined' || !document.activeElement) return;
    const el = document.activeElement as HTMLElement;
    const tag = el?.tagName?.toLowerCase();
    const isInputLike = tag === 'input' || tag === 'textarea' || tag === 'select' || el?.getAttribute?.('contenteditable') === 'true';
    if (isInputLike && el !== document.body && typeof el.blur === 'function') {
      el.blur();
    }
  }, []);

  const handleDatasheetElementsUndo = React.useCallback(async () => {
    if (datasheetEditLocked) return;
    if (isUndoRedoInProgressRef.current) return;
    isUndoRedoInProgressRef.current = true;

    blurActiveInputIfAny();
    // Yield to next macrotask so blur handler and React updates run
    await new Promise<void>((r) => { setTimeout(r, 0); });
    await updatesQueueRef.current;

    const tabId = currentTabIdRef.current;
    const currentHistoryRecordId = currentHistoryRecordIdRef.current;
    const historySequence = datasheetElementsStorage.getHistorySequence(tabId!);

    if (!currentHistoryRecordId || !historySequence || tabId == null) {
      isUndoRedoInProgressRef.current = false;
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    if (currentHistoryIndex < 0) {
      isUndoRedoInProgressRef.current = false;
      return;
    }
    const previousHistoryRecordId = historySequence[currentHistoryIndex - 1];
    const previousHistoryRecord = previousHistoryRecordId
      ? datasheetElementsStorage.getHistoryRecord(tabId, previousHistoryRecordId)
      : undefined;

    if (!previousHistoryRecordId || !previousHistoryRecord) {
      isUndoRedoInProgressRef.current = false;
      return;
    }

    try {
      const currentSnapshot =
        tabId != null
          ? (datasheetElementsStorage.getElements(tabId) || datasheetElementsRef.current)
          : datasheetElementsRef.current;
      await syncBackendForHistoryTransition(currentSnapshot || {}, previousHistoryRecord);

      setTabDatasheetElements({ ...previousHistoryRecord }, tabId);
      datasheetElementsStorage.setCurrentHistoryRecordId(tabId, previousHistoryRecordId);
      currentHistoryRecordIdRef.current = previousHistoryRecordId;
      setCurrentDatasheetElementsHistoryRecordId(previousHistoryRecordId);

      if (typeof sessionStorage !== 'undefined' && globalId != null) {
        sessionStorage.setItem(`DS_TABLE_SYNC_MARKER_${globalId}`, String(Date.now()));
      }

      if (loadTabData && tabId != null && shouldCallLoadTabDataAfterUndoRedo(tableFieldId)) {
        await Promise.resolve(
          loadTabData(Number(tabId), { forceRefresh: true, silent: true })
        );
        notifyTableSyncMarkerConsumed?.();
      }
    } finally {
      isUndoRedoInProgressRef.current = false;
    }
  // syncBackendForHistoryTransition is defined later; captured by closure at invoke time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTabDatasheetElements, loadTabData, tableFieldId, datasheetEditLocked, globalId, notifyTableSyncMarkerConsumed]);

  const handleDatasheetElementsRedo = React.useCallback(async () => {
    if (datasheetEditLocked) return;
    if (isUndoRedoInProgressRef.current) return;
    isUndoRedoInProgressRef.current = true;

    blurActiveInputIfAny();
    await new Promise<void>((r) => { setTimeout(r, 0); });
    await updatesQueueRef.current;

    const tabId = currentTabIdRef.current;
    const currentHistoryRecordId = currentHistoryRecordIdRef.current;
    const historySequence = datasheetElementsStorage.getHistorySequence(tabId!);

    if (!currentHistoryRecordId || !historySequence || tabId == null) {
      isUndoRedoInProgressRef.current = false;
      return;
    }

    const currentHistoryIndex = historySequence.indexOf(currentHistoryRecordId);
    if (currentHistoryIndex < 0) {
      isUndoRedoInProgressRef.current = false;
      return;
    }
    const nextHistoryRecordId = historySequence[currentHistoryIndex + 1];

    if (!nextHistoryRecordId) {
      isUndoRedoInProgressRef.current = false;
      return;
    }

    const nextHistoryRecord = datasheetElementsStorage.getHistoryRecord(tabId, nextHistoryRecordId);

    if (!nextHistoryRecord) {
      isUndoRedoInProgressRef.current = false;
      return;
    }

    try {
      const currentSnapshot =
        tabId != null
          ? (datasheetElementsStorage.getElements(tabId) || datasheetElementsRef.current)
          : datasheetElementsRef.current;
      await syncBackendForHistoryTransition(currentSnapshot || {}, nextHistoryRecord);

      setTabDatasheetElements({ ...nextHistoryRecord }, tabId);
      datasheetElementsStorage.setCurrentHistoryRecordId(tabId, nextHistoryRecordId);
      currentHistoryRecordIdRef.current = nextHistoryRecordId;
      setCurrentDatasheetElementsHistoryRecordId(nextHistoryRecordId);

      if (typeof sessionStorage !== 'undefined' && globalId != null) {
        sessionStorage.setItem(`DS_TABLE_SYNC_MARKER_${globalId}`, String(Date.now()));
      }

      if (loadTabData && tabId != null && shouldCallLoadTabDataAfterUndoRedo(tableFieldId)) {
        await Promise.resolve(
          loadTabData(Number(tabId), { forceRefresh: true, silent: true })
        );
        notifyTableSyncMarkerConsumed?.();
      }
    } finally {
      isUndoRedoInProgressRef.current = false;
    }
  // syncBackendForHistoryTransition is defined later; captured by closure at invoke time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setTabDatasheetElements, loadTabData, tableFieldId, datasheetEditLocked, globalId, notifyTableSyncMarkerConsumed]);


  const getDatasheetElement = React.useCallback((id: DatasheetElementId) => {
    return datasheetElements[id];
  }, [datasheetElements]);

  const buildUpdateFieldValuePayload = React.useCallback((
    element: DatasheetElement<DatasheetFieldComponentGetDto>,
    value: string | number,
    rrn?: number,
    tableId?: number
  ): UpdateFieldValueParams | null => {
    if (!context || !globalId) {
      return null;
    }

    if (element.type !== DatasheetElementType.field) {
      return null;
    }

    const field = element.original.field;
    const fieldId = field.fieldid ?? field.id;
    const systemField = field.systemField ?? false;
    let fieldContext = context;

    if (context === 8 && tableContext && (tableContext === 1 || tableContext === 2)) {
      fieldContext = tableContext;
    }
    if (field.fieldcontext) {
      const contextStr = String(field.fieldcontext);
      if (contextStr === 'IDS_CONTEXT' || contextStr === '1') {
        fieldContext = 1;
      } else if (contextStr === 'PDS_CONTEXT' || contextStr === '2') {
        fieldContext = 2;
      } else {
        const parsed = parseInt(contextStr, 10);
        if (!isNaN(parsed)) {
          fieldContext = parsed;
        }
      }
    }

    return {
      globalId,
      fieldId,
      context: fieldContext,
      systemField,
      ds: true,
      value,
      rrn: rrn ?? -1,
      tableId: tableId ?? 0,
    };
  }, [context, tableContext, globalId]);

  const syncBackendForHistoryTransition = React.useCallback(
    async (
      fromSnapshot: Record<DatasheetElementId, DatasheetElement>,
      toSnapshot: Record<DatasheetElementId, DatasheetElement>
    ) => {
      if (!context || !globalId) {
        return;
      }

      const allIds = new Set<string>([
        ...Object.keys(fromSnapshot || {}),
        ...Object.keys(toSnapshot || {}),
      ]);

      for (const id of allIds) {
        const fromEl = fromSnapshot[id];
        const toEl = toSnapshot[id];

        if (!fromEl || !toEl) {
          continue;
        }

        if (fromEl.type !== DatasheetElementType.field || toEl.type !== DatasheetElementType.field) {
          continue;
        }

        const fromFieldEl = fromEl as DatasheetElement<DatasheetFieldComponentGetDto>;
        const toFieldEl = toEl as DatasheetElement<DatasheetFieldComponentGetDto>;

        const fromField: any = fromFieldEl.original.field;
        const toField: any = toFieldEl.original.field;

        const isTableField =
          fromField.type === FieldType.table
          || fromField.fieldtype?.typeid === FieldType.table
          || fromField.table === true
          || toField.type === FieldType.table
          || toField.fieldtype?.typeid === FieldType.table
          || toField.table === true;
        const hasSubfields =
          ((fromFieldEl.original as any).m_ds_subfields && (fromFieldEl.original as any).m_ds_subfields.length > 0)
          || ((toFieldEl.original as any).m_ds_subfields && (toFieldEl.original as any).m_ds_subfields.length > 0);

        if (isTableField || hasSubfields) {
          const tableIdValue = toField.fieldid ?? toField.id ?? fromField.fieldid ?? fromField.id;
          const systemField = toField.systemField ?? fromField.systemField ?? false;
          const fromTableData: any[] = Array.isArray(fromField.tableData) ? fromField.tableData : [];
          const toTableData: any[] = Array.isArray(toField.tableData) ? toField.tableData : [];

          if (JSON.stringify(fromTableData) === JSON.stringify(toTableData)) {
            continue;
          }

          const fromRrns = new Set(fromTableData.map((r: any) => r.rrn).filter((rrn: any) => rrn != null && rrn > 0));
          const toRrns = new Set(toTableData.map((r: any) => r.rrn).filter((rrn: any) => rrn != null && rrn > 0));

          for (const row of toTableData) {
            const rrn = row?.rrn;
            if (rrn == null || rrn <= 0) continue;
            if (!fromRrns.has(rrn)) {
              try {
                await addDatasheetTableRow({
                  globalId,
                  context,
                  tableId: tableIdValue,
                  systemField,
                  copyFromRrn: -1,
                });
              } catch {
                void 0;
              }
            }
          }

          for (const row of fromTableData) {
            const rrn = row?.rrn;
            if (rrn == null || rrn <= 0) continue;
            if (!toRrns.has(rrn)) {
              try {
                await deleteDatasheetTableRow({
                  globalId,
                  context,
                  tableId: tableIdValue,
                  systemField,
                  rrn,
                });
              } catch {
                void 0;
              }
            }
          }

          for (const toRow of toTableData) {
            const rrn = toRow?.rrn;
            if (rrn == null || rrn <= 0) continue;
            if (!fromRrns.has(rrn)) continue;

            const fromRow = fromTableData.find((r: any) => r.rrn === rrn);
            if (!fromRow) continue;

            const fromCells: any[] = Array.isArray(fromRow.data) ? fromRow.data : [];
            const toCells: any[] = Array.isArray(toRow.data) ? toRow.data : [];

            const allCellFieldIds = new Set<number>();
            fromCells.forEach((c: any) => { if (c?.fieldId != null) allCellFieldIds.add(c.fieldId); });
            toCells.forEach((c: any) => { if (c?.fieldId != null) allCellFieldIds.add(c.fieldId); });

            for (const cellFieldId of allCellFieldIds) {
              const fromCell = fromCells.find((c: any) => c?.fieldId === cellFieldId);
              const toCell = toCells.find((c: any) => c?.fieldId === cellFieldId);

              const fromCellValue = fromCell?.dataValue ?? fromCell?.rawValue ?? fromCell?.displayValue ?? '';
              const toCellValue = toCell?.dataValue ?? toCell?.rawValue ?? toCell?.displayValue ?? '';

              if (String(fromCellValue) === String(toCellValue)) {
                continue;
              }

              try {
                await updateFieldValue({
                  globalId,
                  fieldId: cellFieldId,
                  context,
                  systemField: toCell?.system ?? fromCell?.system ?? false,
                  ds: true,
                  value: toCellValue ?? '',
                  rrn,
                  tableId: tableIdValue,
                });
                if (formatOnly) {
                  refreshPedigreeSubtextAndSymbols();
                }
              } catch {
                void 0;
              }
            }
          }

          continue;
        }

        if (isServerDerivedDatasheetField(fromField) || isServerDerivedDatasheetField(toField)) {
          continue;
        }

        const fieldType =
          toFieldEl.original.field.fieldtype?.typeid
          ?? toFieldEl.original.field.type
          ?? fromFieldEl.original.field.fieldtype?.typeid
          ?? fromFieldEl.original.field.type;

        const oldValue = getFlatFieldEffectiveValue(fromField, fieldType);
        const newValue = getFlatFieldEffectiveValue(toField, fieldType);

        if (oldValue === newValue) {
          continue;
        }

        let processedNewValue: string | number = newValue;

        if ((fieldType === FieldType.number || fieldType === FieldType.integer) && newValue !== '') {
          const numValue = parseFloat(String(newValue));
          if (!isNaN(numValue)) {
            processedNewValue = fieldType === FieldType.integer ? Math.floor(numValue) : numValue;
          }
        }

        let rrn: number | undefined;
        let tableIdForPayload: number | undefined;

        if (context === 8 && tableFieldId != null && tableFieldId > 0) {
          rrn = currentTdsRrn != null && currentTdsRrn > 0 ? currentTdsRrn : -1;
          tableIdForPayload = tableFieldId;
        }

        const payload = buildUpdateFieldValuePayload(
          toFieldEl,
          processedNewValue,
          rrn,
          tableIdForPayload
        );

        if (!payload) {
          continue;
        }

        try {
          await updateFieldValue(payload);
          if (formatOnly) {
            refreshPedigreeSubtextAndSymbols();
          }
        } catch {
          void 0;
        }
      }
    },
    [context, globalId, tableFieldId, currentTdsRrn, buildUpdateFieldValuePayload, formatOnly]
  );

  const updateDatasheetElement = React.useCallback(async (id: DatasheetElementId, data: DatasheetElement) => {
    if (datasheetEditLocked) {
      return Promise.resolve(undefined);
    }
    const runUpdate = async (): Promise<Record<DatasheetElementId, DatasheetElement> | undefined> => {
      const baseElements =
        currentTabIdRef.current != null
          ? (datasheetElementsStorage.getElements(currentTabIdRef.current) || datasheetElementsRef.current)
          : datasheetElementsRef.current;

      const datasheetElement =
        (baseElements[id] as DatasheetElement | undefined) ?? getDatasheetElement(id);

      if (!datasheetElement) {
        return;
      }

    let hasValueChanged = true;

    // Check if this is a field element and if the value has changed
    if (datasheetElement.type === DatasheetElementType.field && data.type === DatasheetElementType.field) {
      const fieldElement = datasheetElement as DatasheetElement<DatasheetFieldComponentGetDto>;
      const newFieldElement = data as DatasheetElement<DatasheetFieldComponentGetDto>;
      const isTableField = fieldElement.original.field.type === FieldType.table;
      const hasSubfields = fieldElement.original.m_ds_subfields && fieldElement.original.m_ds_subfields.length > 0;

      const fieldType = fieldElement.original.field.fieldtype?.typeid ?? fieldElement.original.field.type;
      let newValue: string | number = '';
      // Table fields (and dropdowns/cells within them) store data in field.tableData.
      // Compare tableData so each cell/edit adds its own undo step (matches GWT per-cell undo).
      if (isTableField || hasSubfields) {
        const oldTableData = (fieldElement.original.field as any).tableData ?? [];
        const newTableData = (newFieldElement.original.field as any).tableData ?? [];
        hasValueChanged = JSON.stringify(oldTableData) !== JSON.stringify(newTableData);
      } else {
        const oldEff = getFlatFieldEffectiveValue(fieldElement.original.field as any, fieldType);
        const newEff = getFlatFieldEffectiveValue(newFieldElement.original.field as any, fieldType);
        hasValueChanged = oldEff !== newEff;
        newValue = newEff;
      }

      // Convert to number for number/integer fields if needed
      let processedNewValue: string | number = newValue;

      // For checkbox fields, value is "Y" or "N" - keep as string
      // For dropdown fields, value is the option ID - keep as string/number
      // For number/integer fields, convert string to number
      if ((fieldType === FieldType.number || fieldType === FieldType.integer) && newValue !== '') {
        const numValue = parseFloat(String(newValue));
        if (!isNaN(numValue)) {
          processedNewValue = fieldType === FieldType.integer ? Math.floor(numValue) : numValue;
        }
      }
      
      // For table fields, we need rrn and tableId
      // For now, we'll use placeholder values - these will be extracted from table row changes later
      let rrn: number | undefined;
      let tableId: number | undefined;
      
      if (isTableField || hasSubfields) {
        // Table field - rrn and tableId will be extracted from table row updates later
        // For now, using placeholder values
        rrn = -1;
        tableId = fieldElement.original.field.fieldid ?? fieldElement.original.field.id;
      } else if (context === 8 && tableFieldId != null && tableFieldId > 0) {
        // TDS result fields are flat fields, but updateValue still requires rrn
        // and parent tableId to persist edits to the selected row.
        rrn = currentTdsRrn != null && currentTdsRrn > 0 ? currentTdsRrn : -1;
        tableId = tableFieldId;
      }

      // Build API payload with the actual changed value
      const payload = buildUpdateFieldValuePayload(
        newFieldElement,
        processedNewValue, // Use the processed value (string or number)
        rrn,
        tableId
      );

      if (!isTableField && !hasSubfields && payload && hasValueChanged) {
        try {
          const response = await updateFieldValue(payload);
          if (response?.code && response.code !== 200) {
            const errors = response.errors ?? [];
            const errorMessage = errors.join('\n') || trDts('Validation failed');
            showMessage({
              header: trDts('Validation Error'),
              content: errorMessage,
              confirmText: trDts('OK'),
            });
            const fieldId = fieldElement?.original?.field?.fieldid ?? fieldElement?.original?.field?.id;
            if (fieldId != null) {
              fieldValidationState.setFieldError(fieldId, errorMessage);
            }
            return;
          }
          const fieldId = fieldElement?.original?.field?.fieldid ?? fieldElement?.original?.field?.id;
          if (fieldId != null) {
            fieldValidationState.clearFieldError(fieldId);
          }
          if (formatOnly) {
            refreshPedigreeSubtextAndSymbols();
          }
          if (typeof sessionStorage !== "undefined" && globalId != null) {
            sessionStorage.setItem(`DS_TABLE_SYNC_MARKER_${globalId}`, String(Date.now()));
          }
          if (loadTabData && currentTabIdRef.current != null) {
            await Promise.resolve(
              loadTabData(Number(currentTabIdRef.current), { forceRefresh: true, silent: true })
            );
            notifyTableSyncMarkerConsumed?.();
          }
        } catch {
          void 0;
        }
      }
    }

    const nextDatasheetElements = {
      ...baseElements,
      [id]: data,
    };
    if (hasValueChanged) {
      setTabDatasheetElementsAndAddHistoryRecord(nextDatasheetElements);
    } else {
      setTabDatasheetElements(nextDatasheetElements);
    }

    return nextDatasheetElements;
    };

    // Serialize updates so baseElements includes prior edits (e.g. first name before DOB)
    const queued = updatesQueueRef.current.then(
      () => runUpdate(),
      () => runUpdate()
    );
    updatesQueueRef.current = queued.then(() => {});
    return queued;
  }, [
    datasheetEditLocked,
    getDatasheetElement,
    setTabDatasheetElementsAndAddHistoryRecord,
    setTabDatasheetElements,
    currentTabId,
    buildUpdateFieldValuePayload,
    loadTabData,
    updateFieldValue,
    context,
    tableFieldId,
    currentTdsRrn,
    formatOnly,
    showMessage,
    fieldValidationState,
    notifyTableSyncMarkerConsumed,
  ]);

  const datasheetElementsProcessed = React.useMemo(() => {
    return Object.values(datasheetElements);
  }, [datasheetElements]);

  const pageBreaks = {
    show: datasheet.gridSettings.showPageBreaks,
    height: 1015,
    width: 582,
  };

  const gridSettingsMargin = React.useMemo(() => {
    const dimensions: Record<DatasheetGridSettingsMarginType, { width: number; height: number }> = {
      [DatasheetGridSettingsMarginType.fhq]: {
        width: 1100,
        height: 450,
      },
      [DatasheetGridSettingsMarginType.tablet]: {
        width: 300,
        height: 650,
      },
      [DatasheetGridSettingsMarginType.custom]: {
        width: datasheet.gridSettings.marginWidth,
        height: datasheet.gridSettings.marginHeight
      },
    };

    if (datasheet.gridSettings.marginType) {
      return {
        show: datasheet.gridSettings.showMargins,
        ...dimensions[datasheet.gridSettings.marginType],
      };
    }

    return {
      show: false,
      width: 0,
      height: 0,
    };
  }, [datasheet.gridSettings]);

  return {
    updateDatasheetElement,
    currentTabId,
    handleSelectedTabChange,
    datasheetElementsProcessed,
    gridSettingsMargin,
    pageBreaks,
    validationMessageProps,
    closeValidationMessage: closeMessage,
    fieldValidationState,
    ...tabsController,
    ...undoRedoController,
    handleDatasheetElementsRedo,
    handleDatasheetElementsUndo,
  };
};